import React, { useCallback } from 'react';

import {
  ActionIcon,
  Group,
  Paper,
  Skeleton,
  Title,
  Tooltip,
  createStyles,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { DatePickerInput, DateValue } from '@mantine/dates';
import { useMediaQuery } from '@mantine/hooks';
import { IconFilterOff } from '@tabler/icons-react';
import BackButton from 'components/BackButton';
import DownloadCSV from 'components/DownloadCSV';
import HelpButton from 'components/HelpButton';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import usePayingMembers from 'hooks/reports/athletes/usePayingMembers';
import { useNavigate } from 'react-router-dom';
import { MemberReport } from 'types';
import { formatGymDate, setDateDifference } from 'utils/date-functions';

import { SearchFilters } from '../types';

const useStyles = createStyles(theme => ({
  header: {
    width: '100%',
    background: theme.colors.gray[0],
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    display: 'grid',
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: 0,
    gridTemplateColumns: 'min-content auto max-content min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"backButton title dateInput clear download"`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'min-content auto min-content min-content',
      gridTemplateAreas: `
        'backButton title clear download'
        'dateInput dateInput dateInput dateInput'
        `,
    },
    [theme.fn.smallerThan('sm')]: {
      padding: theme.spacing.sm,
      gridTemplateColumns: 'min-content auto min-content',
      gridTemplateAreas: `
        'backButton title clear'
        'dateInput dateInput dateInput'
        `,
    },
  },
  backButton: {
    gridArea: 'backButton',
  },
  title: {
    gridArea: 'title',
  },
  dateInput: {
    gridArea: 'dateInput',
  },
  clear: {
    gridArea: 'clear',
  },
  download: {
    gridArea: 'download',
    [theme.fn.smallerThan('sm')]: {
      display: 'none',
    },
  },
}));

const csvOptions = {
  fieldSeparator: ',',
  quoteStrings: '',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true,
};

const dataToRow = (d: MemberReport, timezone?: string): { [key: string]: any } => ({
  'Full Name': d.fullName,
  'Current Status': d.athleteStatus,
  Email: d.email,
  Phone: d.phone,
  // ENG-2344: render the gym-local join date, not the viewer's browser-tz date.
  'Member Since': d.memberSince ? formatGymDate(d.memberSince, timezone, 'YYYY-MM-DD') : '',
});
const PayingMembersPageHeader = ({
  initialSearchFiltersValue,
  searchFilters,
  setSearchFilters,
  orderingValue,
}: {
  initialSearchFiltersValue: SearchFilters;
  searchFilters: SearchFilters;
  setSearchFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
  orderingValue: string;
}): JSX.Element => {
  const theme = useMantineTheme();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { session } = useAuth();

  const { data, isLoading, isSuccess, isError } = usePayingMembers({
    date: searchFilters.date.toISOString(),
  });

  const handleBackClick = useCallback((): void => {
    navigate(-1);
  }, [navigate]);

  const handleDateChange = (value: DateValue): void => {
    if (value instanceof Date) {
      const adjustedDate = setDateDifference(value, 0, 0, 0, 23, 59, 59);
      setSearchFilters(prev => ({ ...prev, date: adjustedDate }));
    }
  };

  const handleClearFilters = (): void => {
    setSearchFilters(initialSearchFiltersValue);
  };

  const getPayingMemberCount = (): JSX.Element => {
    if (isLoading) {
      return <Skeleton height='2rem' width='5rem' />;
    }
    if (isError) {
      return (
        <Title order={isDesktop ? 1 : 3} color='red'>
          Error
        </Title>
      );
    }
    if (isSuccess && data.pages) {
      return (
        <Title order={isDesktop ? 1 : 3} color='gray'>
          {data.pages[0].data.count}
        </Title>
      );
    }
    return <></>;
  };

  return (
    <Paper className={classes.header}>
      <BackButton handleOnClick={handleBackClick} />
      <Group noWrap spacing='xl' className={classes.title} data-cy='payingMembersPageHeader'>
        <Title order={isDesktop ? 1 : 3}>Paying Members</Title>
        {getPayingMemberCount()}
        <HelpButton width={400} position={isDesktop ? 'right' : 'bottom'}>
          <Text size='sm'>Athletes with the Member status as of the selected date.</Text>
        </HelpButton>
      </Group>
      <DatePickerInput
        className={classes.dateInput}
        value={searchFilters.date}
        onChange={handleDateChange}
        valueFormat='MMMM DD, YYYY'
        type='default'
        maxDate={new Date()}
        allowDeselect={false}
        required
      />
      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters}>
          <IconFilterOff />
        </ActionIcon>
      </Tooltip>

      <DownloadCSV
        useList={usePayingMembers}
        dataToRow={d => dataToRow(d, session?.gym?.timezone)}
        csvOptions={csvOptions}
        hookOptions={{ ordering: orderingValue, date: searchFilters.date.toISOString() }}
        className={classes.download}
        reportType='paying-members'
        startDate={dayjs(searchFilters.date).format('YYYY-MM-DD')}
        endDate={dayjs(searchFilters.date).format('YYYY-MM-DD')}
      />
    </Paper>
  );
};

export default PayingMembersPageHeader;
